import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import { usePageContext } from "../../../context/pageContext";
import { useI18n } from "../../../hooks/useTranslations";
import useProcessingRequestApi from "../../../http/processingRequest/api";
import { InsertProcessingRequest, InsertProcessingRequestFinalItem } from "../../../http/processingRequest/types";
import { ProcessingRequestFinalItem } from "../../../http/processingRequestFinalItem/types";
import Box from "../../ui/Box";
import Button from "../../ui/Button";
import SubmitBar from "../../ui/SubmitBar";
import styles from "./CreateProcessingRequest.module.css";
import GeneralDataForm, { GeneralDataFormBody, GeneralDataFormHandle } from "./GeneralDataForm";
import ItemsForm, { ItemsFormHandle } from "./ItemsForm";

type CreateProcessingRequestFormBody = {
  generalData: GeneralDataFormBody;
  finalItems: ProcessingRequestFinalItem[];
};

type CreateProcessingRequestProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
};

const CreateProcessingRequest: FC<CreateProcessingRequestProps> = ({ isDisabled, isLoading }) => {
  const pageContext = usePageContext();
  const i18n = useI18n();
  const processingRequestApi = useProcessingRequestApi();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const generalDataRef = useRef<GeneralDataFormHandle>(null);
  const itemsRef = useRef<ItemsFormHandle>(null);

  const mainBoxRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  const [state, setState] = useState<CreateProcessingRequestFormBody>();

  useEffect(() => {
    setHeight(mainBoxRef.current?.clientHeight);
  }, []);

  const onSave = async (state: CreateProcessingRequestFormBody) => {
    try {
      const save = async () => {
        // if (state?.itemsData.isKit) {
        //   const initialItems: InsertProcessingRequestInitialItemBody[] =
        //     state.itemsData.initialItems?.map((ii) => ({
        //       itemId: ii.item.id,
        //       quantity: ii.quantity,
        //     })) ?? [];

        //   const insertData: InsertProcessingRequestForKit = {
        //     ...buildCommon,
        //     initialItems,
        //     finalItemId: state.itemsData.finalItem?.id ?? "",
        //     finalItemQuantity: state.itemsData.finalItemQuantity ?? 0,
        //     isKit: true,
        //   };
        //   const created = await processingRequestApi.createProcessingRequestForKit(insertData);
        //   return created.id;
        // }

        const finalItems: InsertProcessingRequestFinalItem[] =
          state?.finalItems?.map((fi) => ({
            finalItemID: fi.item.id,
            finalItemQuantity: fi.quantity,
            initialItems: fi.initialItems?.map((ii) => ({
              itemID: ii.item.id,
              quantity: ii.quantity,
            })),
          })) ?? [];

        const insertData: InsertProcessingRequest = {
          typeId: state.generalData.type?.id!,
          priorityId: state.generalData.priority.id!,
          notes: state.generalData.notes ?? null,
          supplierCode: state.generalData.supplier?.code ?? null,
          costCenterCode: state.generalData.costCenter?.code ?? null,
          pickingNumber: state.generalData.pickingNumber ?? null,
          offerMandatory: state.generalData.offerMandatory ?? false,
          offerAcceptor: state.generalData.offerAcceptor?.id ?? null,
          finalItems,
          isKit: state.generalData.isKit,
        };
        const created = await processingRequestApi.createProcessingRequest(insertData);
        return created.id;
      };

      const newId = await save();
      navigate(`/requests/${newId}`);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  useEffect(() => {
    pageContext.setPage({ url: "requests", title: i18n.t.common.requests }, i18n.t.common.new);
  }, [i18n.language]);

  const save = async () => {
    const generalDataSuccess = await generalDataRef.current?.validate();
    const itemsSuccess = await itemsRef.current?.validate();
    generalDataSuccess && itemsSuccess && state && (await onSave(state));
    setHeight(mainBoxRef.current?.clientHeight);
  };

  return (
    <>
      <div className={styles.outerContainer}>
        <Box fullHeight type="secondary" ref={mainBoxRef}>
          <div className={styles.mainForm}>
            <GeneralDataForm
              ref={generalDataRef}
              onChange={async (body, valid) => {
                valid &&
                  setState((prev) => ({
                    generalData: body,
                    finalItems: prev?.finalItems ?? [],
                  }));

                setHeight(mainBoxRef.current?.clientHeight);
              }}
              isDisabled={isDisabled}
              isLoading={isLoading}
            />
          </div>
        </Box>

        <Box fullHeight type="secondary">
          <div className={styles.rightForm} style={{ minHeight: height }}>
            {state?.generalData.type ? (
              <ItemsForm
                ref={itemsRef}
                isDisabled={isDisabled}
                isLoading={isLoading}
                allowQuantityVariation={state.generalData.type.allowQuantityVariation}
                initialItemIsFinalItem={state.generalData.type.initialItemIsFinalItem}
                isKitRequest={state.generalData.isKit}
                onChange={async (body, valid) => {
                  valid &&
                    setState({
                      generalData: state.generalData,
                      finalItems: body,
                    });
                }}
              />
            ) : (
              <div className={styles.disabledText}>{i18n.t.common.selectRequestType}</div>
            )}
          </div>
        </Box>
      </div>
      <SubmitBar>
        <Button buttonProps={{ onClick: save }}>{i18n.t.common.save}</Button>
      </SubmitBar>
    </>
  );
};

export default CreateProcessingRequest;
